import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/toaster';
import MiraGlobalMount from './components/MiraGlobalMount';
import MobileFoodSticky from './components/food/MobileFoodSticky';
import AnnouncementBanner from './components/AnnouncementBanner';
import ScrollToTop from './components/ScrollToTop';
import { initTracking } from './lib/tracking';

initTracking();
import XindingsTooltips from './components/XindingsTooltips';

// Zero-flash redirect to the static /menu-lite.html file. Used by the
// QR-code aliases /mobilemenu and /menu so they behave identically on
// preview (SPA catch-all wins) and prod (Cloudflare static file wins).
// Doing this in useEffect avoids a React-render-then-navigate flash.
function LiteRedirect() {
  React.useEffect(() => {
    window.location.replace('/menu-lite.html');
  }, []);
  return null;
}

// Route-level code splitting so /c/* ships only its own chunk (P1: hills 3G budget).
const Landing = lazy(() => import('./pages/Landing'));
const Partners = lazy(() => import('./pages/Partners'));
const Privacy = lazy(() => import('./pages/Privacy'));
const OutletPage = lazy(() => import('./pages/OutletPage'));
const FranchiseIndex = lazy(() => import('./pages/FranchiseIndex'));
const FranchiseCityPage = lazy(() => import('./pages/FranchiseCityPage'));
const PartnersThankYou = lazy(() => import('./pages/PartnersThankYou'));
const TableLanding = lazy(() => import('./pages/TableLanding'));
const MiraPage = lazy(() => import('./pages/MiraPage'));
const SignYourName = lazy(() => import('./pages/SignYourName'));
const Canvas = lazy(() => import('./pages/Canvas'));
const Capture = lazy(() => import('./pages/Capture'));
const CardThread = lazy(() => import('./pages/CardThread'));
const Showcase = lazy(() => import('./pages/Showcase'));
const Challenges = lazy(() => import('./pages/Challenges'));
const Story = lazy(() => import('./pages/Story'));
const About = lazy(() => import('./pages/About'));
const Xindings = lazy(() => import('./pages/Xindings'));
const Food = lazy(() => import('./pages/Food'));
const Wall = lazy(() => import('./pages/Wall'));
const MadeReal = lazy(() => import('./pages/MadeReal'));
const MadeRealDetail = lazy(() => import('./pages/MadeRealDetail'));
const Series = lazy(() => import('./pages/Series'));
const SeriesWalk = lazy(() => import('./pages/SeriesWalk'));
const CanvasIdea = lazy(() => import('./pages/CanvasIdea'));
const Who = lazy(() => import('./pages/Who'));
const Admin = lazy(() => import('./pages/Admin'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const BlogCategory = lazy(() => import('./pages/BlogCategory'));
const MobileMenu = lazy(() => import('./pages/MobileMenu'));

function Fallback() {
  return <div style={{ minHeight: '60vh' }} aria-busy="true" />;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
        <ScrollToTop />
        <XindingsTooltips />
        <AnnouncementBanner />
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/table" element={<TableLanding />} />
            <Route path="/mira" element={<MiraPage />} />
            <Route path="/sign" element={<SignYourName />} />
            <Route path="/canvas" element={<Canvas />} />
            <Route path="/challenges" element={<Challenges />} />
            <Route path="/c/:code" element={<Capture />} />
            <Route path="/card/:code" element={<CardThread />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="/story" element={<Story />} />
            <Route path="/about" element={<About />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/partner-with-us" element={<Navigate to="/partners" replace />} />
            <Route path="/franchise" element={<FranchiseIndex />} />
            <Route path="/franchise/:city" element={<FranchiseCityPage />} />
            <Route path="/menu" element={<Navigate to="/food" replace />} />
            <Route path="/contact" element={<Navigate to="/xin-hsr" replace />} />
            {['koramangala', 'hsr', 'coonoor', 'ooty'].map((o) => <Route key={o} path={`/xin-${o}`} element={<OutletPage slug={o} />} />)}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/partners/thank-you" element={<PartnersThankYou />} />
            <Route path="/xindings" element={<Xindings />} />
            <Route path="/food" element={<Food />} />
            {/* Aliases that redirect to the fast text menu. The printed
                QR encodes `/mobilemenu`, so diners scanning it land on
                /menu-lite.html (no images, works on 3G).
                • On prod, Cloudflare auto-resolves /mobilemenu → the
                  static mobilemenu.html which does a meta refresh + JS
                  location.replace to /menu-lite.html.
                • On preview (SPA catch-all wins), this React shim runs
                  the same location.replace, so behaviour stays identical
                  across environments. */}
            <Route path="/mobilemenu" element={<LiteRedirect />} />
            <Route path="/menu" element={<LiteRedirect />} />
            <Route path="/wall" element={<Wall />} />
            <Route path="/made-real" element={<MadeReal />} />
            <Route path="/made-real/:code" element={<MadeRealDetail />} />
            <Route path="/series/:seriesId" element={<Series />} />
            <Route path="/series/:seriesId/walk/:seriesCode" element={<SeriesWalk />} />
            <Route path="/canvas/idea/:submissionId" element={<CanvasIdea />} />
            <Route path="/who/:slug" element={<Who />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/category/:slug" element={<BlogCategory />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/mobilemenu" element={<MobileMenu />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <MiraGlobalMount />
          <MobileFoodSticky />
        </Suspense>
      </BrowserRouter>
      <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;
